import Helpers from "./helpers";

declare var bodyScrollLock: any;

export default class ImageZoom {

	private container: HTMLElement;
	private instructionTextElement: HTMLElement;
	private instructionTextInitial = 'Click the image to zoom';
	private instructionTextKeyboardOn = 'Use keyboard arrows to control image position and escape to close';
	private instructionTextTouchOn = 'Drag to control the image position';
	private instructionTextMouseOn = 'Move the zoomed image with your mouse';
	private zoomActive = false;
	private isZoomed = false;
	private baseImageUrl: string;
	private fullImageUrl: string;
	private zoomedImageWidth: number;
	private zoomedImageHeight: number;
	private isUsingTouch = Helpers.isTouch();
	private keyboardControlsFunction: any;
	private containerOffset: {
		top: number,
		left: number
	};
	private currentX = 50;
	private currentY = 50;
	private lastTouchX: number;
	private lastTouchY: number;

	constructor(container: HTMLElement) {

		this.container = container;

		if (this.container.classList.contains('is-done')) {
			return;
		}

		let observer = new IntersectionObserver((entries, self) => {
			entries.forEach(entry => {
				if (entry.isIntersecting) {
					this.setupImage();
					this.attachEvents();
					observer.unobserve(this.container);
				}
			});
		});

		observer.observe(this.container);
		this.instructionTextElement = this.container.closest('.js-image-zoom-container').querySelector('.js-image-zoom-text');
		this.container.classList.add('is-done');

		this.containerOffset = this.getContainerOffset();
	}

	setupImage() {
		const imageWidth = Math.round(Helpers.clamp(this.container.clientWidth * 1.5, 720, 1066));
		const imageHeight = Math.round(Helpers.clamp(this.container.clientHeight * 1.5, 720, 1066));
		// choose the largest so we have a square image
		const imageSize = Math.max(imageWidth, imageHeight);
		this.zoomedImageWidth = imageSize;
		this.zoomedImageHeight = imageSize;
		const imageElement = this.container.querySelector('.js-image-to-zoom') as HTMLImageElement;
		this.baseImageUrl = imageElement.getAttribute('src').split('?')[0];
		this.fullImageUrl = this.baseImageUrl + this.getImageUrlSuffix();
		this.checkIfZoomShouldBeActive();
		requestAnimationFrame(() => {
			this.container.style.backgroundSize = `${this.zoomedImageWidth}px ${this.zoomedImageHeight}px`;
		});
	}

	getImageUrlSuffix() {
		return `?w=${this.zoomedImageWidth}&h=${this.zoomedImageHeight}&mode=pad&scale=upscalecanvas&bgcolor=#ffffff&quality=100`;
	}

	attachEvents() {

		// Mouse events

		const debouncedTouchCheck = Helpers.debounce((e) => {
			const targetElement = e.target as HTMLElement;
			if (targetElement && !targetElement.closest('.js-image-zoom')) {
				this.isUsingTouch = false;
			}
		}, 150);

		document.addEventListener('mousemove', debouncedTouchCheck);

		this.container.addEventListener('mousemove', (e) => {
			if (this.isUsingTouch) {
				return false;
			}
			this.isUsingTouch = false;
			this.zoom(e);
		});

		// Resize

		const debouncedResize = Helpers.debounce((e) => {
			this.containerOffset = this.getContainerOffset();
			this.disableIsZoomed();
			this.setupImage();
		}, 250);

		document.addEventListener('resize', debouncedResize);

		// image click

		this.container.addEventListener('click', () => {

			if (this.isUsingTouch) {
				return;
			}

			if (this.isZoomed) {
				this.disableIsZoomed(this.isUsingTouch);
				if (this.isUsingKeyboard()) {
					this.instructionTextElement.textContent = this.instructionTextInitial;
					document.documentElement.classList.remove('keyboard-image-zoomed');
				}
			} else {
				this.enableIsZoomed(this.isUsingTouch);
				if (this.isUsingKeyboard()) {
					this.instructionTextElement.textContent = this.instructionTextKeyboardOn;
					document.documentElement.classList.add('keyboard-image-zoomed');
				}
			}

		});

		// zoom button

		this.container.querySelector('.js-image-zoom-button').addEventListener('click', (e) => {
			e.stopPropagation();
			if (this.isZoomed) {
				this.disableIsZoomed(this.isUsingTouch);
				if (this.isUsingKeyboard()) {
					this.instructionTextElement.textContent = this.instructionTextInitial;
					document.documentElement.classList.remove('keyboard-image-zoomed');
				}
			} else {
				this.enableIsZoomed(this.isUsingTouch);
				if (this.isUsingKeyboard()) {
					this.instructionTextElement.textContent = this.instructionTextKeyboardOn;
					document.documentElement.classList.add('keyboard-image-zoomed');
				}
			}

		});

		// Touch events
		let touchEnableTimer;
		let touchDisableTimer;

		this.container.addEventListener('touchstart', (e) => {
			this.isUsingTouch = true;
			const targetElement = e.target as HTMLElement;
			if (targetElement && targetElement.closest('.js-image-zoom-button')) {
				return false;
			}
			if (!this.zoomActive) {
				return false;
			}
			if (this.isZoomed) {
				e.preventDefault();
				this.abortEvent(e);
				this.checkIfZoomShouldBeActive();
				touchDisableTimer = setTimeout(() => {
					this.disableIsZoomed(this.isUsingTouch);
				}, 500);
			} else {
				touchEnableTimer = setTimeout(() => {
					this.enableIsZoomed(true);
				}, 500);
			}
		});

		this.container.addEventListener('touchmove', (e) => {
			const targetElement = e.target as HTMLElement;
			if (targetElement && targetElement.closest('.js-image-zoom-button')) {
				return false;
			}

			if (touchEnableTimer) {
				clearTimeout(touchEnableTimer);
			}
			if (touchDisableTimer) {
				clearTimeout(touchDisableTimer);
			}

			if (this.isZoomed) {
				this.abortEvent(e);
				this.zoom(e);
			}
		}, { passive: false });

		this.container.addEventListener('contextmenu', (e) => {
			this.abortEvent(e);
			return false;
		});

		this.container.querySelector('img').addEventListener('contextmenu', (e) => {
			this.abortEvent(e);
			return false;
		});

		document.addEventListener('resetImageZoom', () => {
			if (this.isZoomed) {
				this.disableIsZoomed(window.matchMedia('(max-width: 980px)').matches);
			}
		});

	}

	public setZoomButtonState(showZoomIn: boolean) {

		if (showZoomIn) {
			this.container.querySelector('.js-image-zoom-button-zoom-in').classList.remove('is-hidden');
			this.container.querySelector('.js-image-zoom-button-zoom-out').classList.add('is-hidden');
		} else {
			this.container.querySelector('.js-image-zoom-button-zoom-in').classList.add('is-hidden');
			this.container.querySelector('.js-image-zoom-button-zoom-out').classList.remove('is-hidden');
		}

	}

	enableIsZoomed(lockPage = false) {
		this.isZoomed = true;
		this.container.classList.add('is-zoomed');
		document.documentElement.classList.add('image-zoom-active');
		this.setZoomButtonState(false);
		if (lockPage && !this.container.classList.contains('js-image-zoom-in-overlay')) {
			if (!Helpers.isWholeElementInViewport(this.container)) {
				this.container.scrollIntoView();
			}
			bodyScrollLock.disableBodyScroll(this.container, {
				reserveScrollBarGap: true
			});
		}
		this.attachKeyboardControls(); 

		this.instructionTextElement.textContent = this.isUsingTouch ? this.instructionTextTouchOn : this.instructionTextMouseOn;
	}

	public disableIsZoomed(unLockPage = false) {
		this.isZoomed = false;
		this.container.classList.remove('is-zoomed');
		this.setZoomButtonState(true);
		document.documentElement.classList.remove('image-zoom-active');
		if (unLockPage && !this.container.classList.contains('js-image-zoom-in-overlay')) {
			bodyScrollLock.enableBodyScroll(this.container);
		}
		this.removeKeyboardControls();
		this.instructionTextElement.textContent = this.instructionTextInitial;
	}

	abortEvent(e: any) {
		e.preventDefault();
		e.stopPropagation();
		e.cancelBubble = true;
		e.returnValue = false;
	}

	zoom(e: any) {

		if (!e.target.classList.contains('js-image-zoom')) {
			return;
		}

		if (!this.zoomActive) {
			this.container.style.backgroundPosition = '0% 0%';
			return;
		}

		if (e.offsetX === undefined && e.touches === undefined) {
			return;
		}

		if (!this.containerOffset.left) {
			this.containerOffset = this.getContainerOffset();
		}

		let offsetX: number;
		let offsetY: number;
		if (e.type === "touchmove") {
			offsetX = (e.touches[0].clientX - this.containerOffset.left);
			offsetY = (e.touches[0].clientY - this.containerOffset.top);
		} else {
			offsetX = e.offsetX;
			offsetY = e.offsetY;
		}

		let x;
		let y;

		if (e.type === "touchmove") {
			const newPositions = this.moveTouchPosition(offsetX, offsetY);
			x = newPositions.x;
			y = newPositions.y;
			this.lastTouchX = offsetX;
			this.lastTouchY = offsetY;
		} else {
			x = this.keepWithinRangeOfImage(offsetX / this.container.offsetWidth * 100);
			y = this.keepWithinRangeOfImage(offsetY / this.container.offsetHeight * 100);
		}


		requestAnimationFrame(()=>{
			this.container.style.backgroundPosition = x + '% ' + y + '%';
			this.currentX = x;
			this.currentY = y;
		});
	}

	getContainerOffset() {
		const rect = this.container.getBoundingClientRect();
		return {
			top: rect.top + document.body.scrollTop,
			left: rect.left + document.body.scrollLeft
		}
	}

	moveTouchPosition(positionX, positionY) {

		const touchScale = 0.33;
		let x = this.currentX;
		let y = this.currentY;
		let xDiff = Math.abs(positionX - this.lastTouchX);
		let yDiff = Math.abs(positionY - this.lastTouchY);
		// discard massive changes
		if (xDiff > 50) {
			xDiff = 2;
		}
		if (yDiff > 50) {
			yDiff = 2;
		}
		const xMove = xDiff * touchScale;
		const yMove = yDiff * touchScale;

		if (positionX > this.lastTouchX) {
			x = this.currentX - xMove;
		}

		if (positionX < this.lastTouchX) {
			x = this.currentX + xMove;
		}

		if (positionY > this.lastTouchY) { 
			y = this.currentY - yMove;
		}

		if (positionY < this.lastTouchY) {
			y = this.currentY + yMove;
		}

		return {
			x: this.roundToTwoDecimalPlaces(this.keepWithin0to100(x)),
			y: this.roundToTwoDecimalPlaces(this.keepWithin0to100(y))
		}
	}

	roundToTwoDecimalPlaces(number: number) {
		return Math.round(number * 100) / 100;
	}

	keepWithinRangeOfImage(number: number) {

			const scale = 2;

			// -100 to 100
			number = this.convertToScale(number);

			// scale
			number = number * scale;

			// check if out of range
			if (number > 100) {
				number = 100;
			}
			if (number < -100) {
				number = -100;
			}

			// convert back to 0 to 100
			number = (number / 2) + 50;

			return Math.round(number);

	}

	convertToScale(number: number) {
		return (number - 50) * 2;
	}

	isUsingKeyboard() {
		return document.documentElement.classList.contains('using-keyboard');
	}

	attachKeyboardControls() {
		this.keyboardControlsFunction = (e) => this.keyboardControls(e);
		window.addEventListener('keydown', this.keyboardControlsFunction);
	}

	removeKeyboardControls() {
		if (this.keyboardControlsFunction) {
			window.removeEventListener('keydown', this.keyboardControlsFunction);
		}

	}

	keepWithin0to100(number: number) {

		if (number > 100) {
			number = 100;
		}

		if (number < 0) {
			number = 0;
		}

		return number;

	}

	keyboardControls(e) {

		const currentPosition = this.getCurrentImagePosition();
		const key = Helpers.getKeypressed(e);

		if (key === 'ArrowRight' || key === "Right" || key === 39) {
			const x = this.keepWithin0to100(currentPosition.left + 10);
			const y = currentPosition.top;
			this.container.style.backgroundPosition = x + '% ' + y + '%';
			e.preventDefault();
		}

		if (key === 'ArrowLeft' || key === "Left" || key === 37) {
			const x = this.keepWithin0to100(currentPosition.left - 10);
			const y = currentPosition.top;
			this.container.style.backgroundPosition = x + '% ' + y + '%';
			e.preventDefault();
		}

		if (key === 'ArrowUp' || key === "Up" || key === 38) {
			const x = currentPosition.left;
			const y = this.keepWithin0to100(currentPosition.top - 10);
			this.container.style.backgroundPosition = x + '% ' + y + '%';
			e.preventDefault();
		}

		if (key === 'ArrowDown' || key === "Down" || key === 40) {
			const x = currentPosition.left;
			const y = this.keepWithin0to100(currentPosition.top + 10);
			this.container.style.backgroundPosition = x + '% ' + y + '%';
			e.preventDefault();
		}

		if (key === 'Escape' || key === "Esc" || key === 27) {
			this.disableIsZoomed(true);
		}

	}

	getCurrentImagePosition() {
		const backgroundPositionStyle = window.getComputedStyle(this.container, null).backgroundPosition.trim().split(/\s+/);
		return {
			left: parseFloat(backgroundPositionStyle[0]),
			top: parseFloat(backgroundPositionStyle[1])
		}
	}

	checkIfZoomShouldBeActive() {
		this.getImageSize(this.baseImageUrl)
			.then((originalImageData: any) => {
				const displayImageSize = this.getDisplayImageSize(this.container);
				const zoomShouldBeActive = ((originalImageData.width - displayImageSize.width) / originalImageData.width) > 0.3;
				this.zoomActive = zoomShouldBeActive;
				if (zoomShouldBeActive) {
					this.container.classList.add('is-active');
					this.container.style.backgroundImage = `url("${CSS.escape(this.fullImageUrl)}")`;
				} else {
					this.container.classList.remove('is-active');
				}
			}).catch(() => {
				this.zoomActive = false;
				this.container.classList.remove('is-active');
			});
	}

	getDisplayImageSize(image: HTMLElement) {
		return {
			width: image.clientWidth ? image.clientWidth : image.parentElement.clientWidth,
			height: image.clientHeight ? image.clientHeight : image.parentElement.clientHeight,
		}
	}

	getImageSize(imageSrc: string): Promise<{
		width: number,
		height: number
	}> {
		imageSrc = imageSrc + '?format=webp&quality=1'; // we only need to know the size, so can be low quality
		/* We are polyfilling promise - TypeScript just complains that the outputted version does support Promise */
		//@ts-ignore
		return new Promise(function (resolve, reject) {
			var img = new Image()
			img.onload = function () {
				resolve({
					//@ts-ignore
					width: this.width,
					//@ts-ignore
					height: this.height
				});

				Helpers.removeElement(document.querySelector('img[src="' + imageSrc + '"].js-zoom-size-check'));
			};
			img.onerror = reject;
			img.src = imageSrc;
			img.alt = "";
			img.style.opacity = "0.0001";
			img.style.position = "fixed";
			img.style.pointerEvents = "none";
			img.style.top = "0px";
			img.style.left = navigator.userAgent.toLowerCase().indexOf('firefox') > -1 ? "0px" : "calc(100% - 1px)";
			img.style.zIndex = "100";
			img.style.maxWidth = "none";
			img.classList.add('js-zoom-size-check');
			document.body.appendChild(img);
		});
	}

}
